import type { FC } from 'react';
import { useEffect, useState } from 'react';
import type { ImagePrompt, ImageRange, ImageResolution } from '@/app/types';
import { resizeImage } from '@/app/utils/client';
import { displayRangeToPercent } from '../utils';
import { type CropPercent, PreviewCrop } from './PreviewCrop';

interface ThumbContentProps {
  isActive: boolean;
  prompt: ImagePrompt;
  range?: ImageRange;
  domResolution?: ImageResolution;
}

/**
 * 缩略图内容组件
 * - 激活图片：实时预览（跟随拖动）
 * - 非激活 + 有缓存：显示本次编辑的裁剪预览
 * - 非激活 + 无缓存：显示上次保存的 selectedImagePreview
 */
export const ThumbContent: FC<ThumbContentProps> = ({ isActive, prompt, range, domResolution }) => {
  const [miniUrl, setMiniUrl] = useState<string>('');
  const [cropPercent, setCropPercent] = useState<CropPercent | null>(null);

  // 只在激活状态时生成缩小版图片用于预览
  useEffect(() => {
    if (!isActive) return;

    const baseUrl = prompt.sourceImagePreview;
    if (!baseUrl) return;

    let cancelled = false;
    let objectUrl: string | null = null;

    resizeImage(baseUrl, 600 * 800).then(async (canvas) => {
      if (cancelled || !canvas) return;
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob((b) => resolve(b), 'image/webp', 0.9);
      });
      if (!blob) return;
      objectUrl = URL.createObjectURL(blob);
      setMiniUrl(objectUrl);
    });

    return () => {
      cancelled = true;
      if (objectUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [isActive, prompt.sourceImagePreview]);

  useEffect(() => {
    const tmpCropPercent =
      range && prompt.sourceImageWidth && prompt.sourceImageHeight && domResolution
        ? displayRangeToPercent(
            range,
            { width: prompt.sourceImageWidth, height: prompt.sourceImageHeight },
            domResolution,
          )
        : null;
    // 切换 isActive 时，cropPercent 可能暂时变为 null，这时候不更新，避免切换时闪烁
    if (tmpCropPercent) {
      setCropPercent(tmpCropPercent);
    }
  }, [range, prompt.sourceImageWidth, prompt.sourceImageHeight, domResolution]);

  // 激活图片或有缓存的非激活图片：显示实时裁剪预览
  const naturalSize = {
    width: prompt.sourceImageWidth || 0,
    height: prompt.sourceImageHeight || 0,
  };

  if (cropPercent && prompt.sourceImagePreview) {
    const imageUrl = miniUrl ? miniUrl : prompt.sourceImagePreview;

    return (
      <PreviewCrop imageUrl={imageUrl} cropPercent={cropPercent} naturalSize={naturalSize} className="size-full" />
    );
  }

  // 还没有 cropPercent 的图片：显示上次保存的结果图
  return <img src={prompt.selectedImagePreview ?? ''} alt={''} className="size-full object-contain" />;
};
